import * as React from 'react';
import * as qs from 'qs';
import * as moment from 'moment';
import {
    GroupChart,
    PonderaModal,
    PVAStyle,
    LoadingIndicator,
    PVAContainerSimple,
    PVABadge, Pdf, PVAContainer, FoundationsReport
} from '../../../components';
import { ReportType, MidRangeType } from '../../../enums';
import { ReportDataService, StyleDataService, UserDataService } from '../../../services';
import { Style, StyleText, SurveyNote, UserPermissions, Survey, SurveyVersion, Organization, SurveyReportType, User } from '../../../models';
import { hasPermission } from '../../../utilities/User';
import { BaseRouteProps } from '../../../interfaces';
import { UserStore } from '../../../stores';
import { Roles } from '../../../stores/Constants';
import { computed, IReactionDisposer } from 'mobx';
import { observer, inject } from 'mobx-react';
import { FoundationsReportContent } from '../myreport/FoundationsReport';
import Messages from '../../../utilities/Messages';
import './BriefReport.css';
import './IndividualReportBlock.css';

interface IndividualReportState {
    reportType: ReportType;
    midRangeType: MidRangeType;
    orgId: number;
    surveyVersionId: number;
    open: boolean;
    notes: SurveyNote[];
    workingNote: string;
    showNotes: boolean;
    private: boolean;
    surveyId?: number;
    retainUserId: boolean;
    linkedFrom9Box: boolean;
    small: boolean;
    allStyles?: Style[];
    reportStructure?: any;
}

interface IndividualReportBlockProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    userId?: number;
    loading: boolean;
    reportType?: ReportType;
    midRangeType?: MidRangeType;
    small?: boolean;
    showNotes?: boolean;
    onNotesChange?: (showNotes: boolean) => void;
    pdfLoader?: boolean;
    wrappedComponentRef?: (ref: IndividualReportBlock) => void;
    messages?: string;
    selectedUserData?: any;
    survey?: Survey;
    surveyVersion?: SurveyVersion;
    noSurveys?: boolean;
    organization?: Organization;
    surveyReportType?: SurveyReportType;
    users?: User[];
    teamId?: number;
    showFoundations?: boolean;
}

@inject('UserStore') @observer
export class IndividualReportBlock extends React.Component<IndividualReportBlockProps, IndividualReportState> {
    static SupportedMessages = {
        createBriefReport: 'create brief_report',
        returnBriefReport: 'return brief_report',
    }
    
    private userDataService = new UserDataService(this.props.notificationService);
    private reportDataService = new ReportDataService(this.props.notificationService);
    private pageTop: any;
    private styleDataService = new StyleDataService(this.props.notificationService);
    private disposer: IReactionDisposer;

    @computed get reportType() {
        return this.props.reportType !== undefined ? this.props.reportType : this.state.reportType;
    }

    @computed get midRangeType() {
        return this.props.midRangeType !== undefined ? this.props.midRangeType : this.state.midRangeType;
    }

    @computed get small() {
        return this.props.small !== undefined ? this.props.small : this.state.small;
    }

    @computed get showNotes() {
        return this.props.showNotes !== undefined ? this.props.showNotes : this.state.showNotes;
    }

    @computed get loggedInUserId() {
        return this.props.UserStore!.userInfo.id;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get userCanSeeRIandRB() {
        return this.props.UserStore!.checkUserPermission(UserPermissions.ViewResponseIndicatorsAndBreakdown)
            || this.props.UserStore!.userRoleIn([Roles.SuperAdmin, Roles.SiteAdmin, Roles.PonderaAssociate]);
    }

    private get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get survey() {
        if (this.props.survey) {
            return this.props.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.surveyVersion) {
            return this.props.surveyVersion;
        }
        return null;
    }

    @computed get noSurveys() {
        return !!this.props.noSurveys;
    }
    
    private get emptyReportData() {
        return {
            default: true,
            inclinationGroups: (this.state.reportStructure.inclinationGroups || []).map((group: any) => {
                return {
                    id: group.id,
                    characteristicGroupID: group.id,
                    name: group.name,
                    displayType: group.displayType,
                    surveyReportTypeID: group.surveyReportTypeID,
                    subtitle: group.subtitle,
                    description: group.description,
                    data: (group.data || []).map((cgc: any) => {
                        return {
                            characteristicID: cgc.characteristicID,
                            name: cgc.name,
                            value: 0,
                            midrangeLow: cgc.midrangeLow,
                            midrangeHigh: cgc.midrangeHigh,
                            helpText: cgc.helpText,
                            description: cgc.description,
                            tooltip: cgc.tooltip,
                            inclinationType: 'normal',
                            teamMidRanges: []
                        };
                    }),
                    subGroups: (group.subGroups || []).map((sg: any) => {
                        return {
                            name: sg.name,
                            data: (sg.data || []).map((csgc: any) => {
                                return {
                                    characteristicID: csgc.characteristicID,
                                    name: csgc.name,
                                    value: 0,
                                    midrangeLow: csgc.midrangeLow,
                                    midrangeHigh: csgc.midrangeHigh,
                                    helpText: csgc.helpText,
                                    description: csgc.description,
                                    tooltip: csgc.tooltip,
                                    inclinationType: 'normal',
                                    teamMidRanges: []
                                };
                            })
                        };
                    })
                };
            })
        };
    }

    @computed get selectedUserData() {
        if (this.props.selectedUserData) {
            return this.props.selectedUserData;
        }

        if (this.surveyVersion && this.state.reportStructure) {
            return this.emptyReportData;
        }
        
        return null;
    }

    constructor(props: IndividualReportBlockProps) {
        super(props);

        const userId = qs.parse(this.props.location.search.replace('?', '')).uid || this.props.userId;

        this.state = {
            reportType: ReportType.Standard,
            midRangeType: MidRangeType.Default,
            orgId: 0,
            surveyId: 0,
            surveyVersionId: 0,
            open: false,
            notes: [],
            workingNote: '',
            showNotes: false,
            private: true,
            retainUserId: userId ? true : false,
            linkedFrom9Box: userId ? true : false,
            small: false,
            allStyles: [],
            reportStructure: null
        };
    }

    componentDidMount() {
        if (this.props.messages) {
            Messages.listen(IndividualReportBlock.SupportedMessages.createBriefReport, async () => {
                await Messages.send(IndividualReportBlock.SupportedMessages.returnBriefReport, this.renderBriefReport());
            });
        }

        if (this.props.selectedUserData && this.props.selectedUserData.reportStructure) {
            this.setState({ reportStructure: this.props.selectedUserData.reportStructure });
        } else if (this.surveyVersion && !this.state.reportStructure) {
            this.reportDataService.getIndividualReportStructureData(this.surveyVersion.id).then((data: any) => {
                this.setState({ reportStructure: data });
            });
        }

        if (this.state.allStyles!.length < 1 && this.surveyVersion) {
            if (this.props.selectedUserData && this.props.selectedUserData.allStyles) {
                this.setState({ allStyles: this.props.selectedUserData.allStyles });
            } else {
                this.styleDataService.getAllBySurveyVersionId(this.surveyVersion!.id).then((styles: Style[]) => {
                    this.setState(() => { return { allStyles: styles }; } );
                });
            }
        }
    }

    componentDidUpdate(prevProps: IndividualReportBlockProps) {
        if (this.surveyVersion && (!prevProps.surveyVersion || prevProps.surveyVersion.id !== this.surveyVersion.id)) {
            if (this.props.selectedUserData && this.props.selectedUserData.reportStructure && this.props.selectedUserData.surveyVersionId === this.surveyVersion.id) {
                this.setState({ reportStructure: this.props.selectedUserData.reportStructure });
            } else {
                this.reportDataService.getIndividualReportStructureData(this.surveyVersion.id).then((data: any) => {
                    this.setState({ reportStructure: data });
                });
            }
        }

        if (this.props.selectedUserData !== prevProps.selectedUserData && this.props.selectedUserData) {
            if (this.props.selectedUserData.reportStructure) {
                this.setState({ reportStructure: this.props.selectedUserData.reportStructure });
            }
            if (this.props.selectedUserData.allStyles) {
                this.setState({ allStyles: this.props.selectedUserData.allStyles });
            }
        }

        if (this.props.organization && (!prevProps.organization || prevProps.organization.id !== this.props.organization.id)) {
            this.setState({ allStyles: [] });
            if (this.surveyVersion) {
                this.styleDataService.getAllBySurveyVersionId(this.surveyVersion.id).then((styles: Style[]) => {
                    this.setState({ allStyles: styles });
                });
            }
        }
    }
    
    @computed get organizationName() {
        if (this.props.organization) {
            return `${this.props.organization.name}`;
        }
        return '';
    }
    
    @computed get organization() {
        if (this.props.organization) {
            return this.props.organization;
        }
        return null;
    }

    @computed get surveyReportType() {
        if (this.props.surveyReportType) {
            return this.props.surveyReportType;
        }
        return null;
    }

    @computed get users() {
        if (this.props.users) {
            return this.props.users;
        }
        return [];
    }

    @computed get teamID() {
        if (this.props.teamId) {
            return this.props.teamId;
        }
        return undefined;
    }

    private GetTeamID() {
        var result = this.teamID;
        if (result && result > 0) {
            return result;
        }
        if (this.selectedUserData) {
            return this.selectedUserData.teamID;
        }
        return undefined;
    }

    private setNotes(showNotes: boolean) {
        if (this.props.onNotesChange) {
            this.props.onNotesChange(showNotes);
        } else {
            this.setState({ showNotes, open: false });
        }
    }

    // exportPDF = () => {
    //     if (this.PdfExportEnabled()) {
    //         this.setPdfLoader(true);
    //         this.setState({ open: false });
    //         this.reportDataService.getIndividualReportDataPDF(this.selectedUserData.surveyInstanceId, 'PVA Individual Report', this.surveyReportType ? this.surveyReportType.id : undefined).then((reportData: any) => {
    //             this.setPdfLoader(false);
    //             })
    //             .catch((error) => {
    //                 console.log(error);
    //                 this.setPdfLoader(false);
    //             });
    //     }
    // }
    //
    // private exportBriefPDF = () => {
    //     if (this.PdfExportEnabled()) {
    //         this.setPdfLoader(true);
    //         this.setState({ open: false });
    //         this.reportDataService.getIndividualBriefPDF(this.selectedUserData.surveyInstanceId, 'PVA Brief Report', this.surveyReportType ? this.surveyReportType.id : undefined).then((reportData: any) => {
    //             this.setPdfLoader(false);
    //         })
    //         .catch((error) => {
    //             console.log(error);
    //             this.setPdfLoader(false);
    //         });
    //     }
    // }
    //
    // private exportLimitedStylePDF() {
    //     this.setPdfLoader(true);
    //     this.setState({ open: false });
    //     this.reportDataService.getLimitedStylePDF(this.getCurrentStyle().id, this.getCurrentStyle().name).then(() => {
    //         this.setPdfLoader(false);
    //     }).catch((error) => {
    //         console.log(error);
    //         this.setPdfLoader(false);
    //     });
    // }
    //
    // private exportNewUserGuidePDF() {
    //     this.setPdfLoader(true);
    //     this.setState({ open: false });
    //     this.reportDataService.getNewUserGuidePDF("PVA Style Report User’s Guide").then(() => {
    //         this.setPdfLoader(false);
    //     }).catch((error) => {
    //         console.log(error);
    //         this.setPdfLoader(false);
    //     });
    // }

    private HasAccessToMidrange() {
        return this.HasAccessToViewMidranges() && this.props.UserStore!.checkUserPermission(UserPermissions.CanViewMidRangeChoices);
    }
    private HasAccessToViewMidranges() {
        return (this.props.UserStore!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewMidRanges));
    }

    PdfExportEnabled = () => {
        return this.props.showFoundations || !!this.props.userId;
    }

    private getUserStyle() {
        if (this.selectedUserData && this.selectedUserData.style) {
            return this.selectedUserData.style;
        }
        return '';
    }

    content(type: string) {
        const styleTexts = this.getUserStyle().styleTexts || [];
        var result = styleTexts.find((st: StyleText) => {
            return st.styleTextType === type;
        });
        if (result) {
            return result.value;
        }
        return '';
    }

    public render() {
        if (this.props.loading) {return <LoadingIndicator />;}

        let userStyle = this.getUserStyle();
        
        if (!this.selectedUserData || this.selectedUserData.isDefault) {
            return null;
        }

        let defaultOptions = [{
            id: 0,
            value: '- Clear Selected -'
        }];

        if (this.props.showFoundations) {
            if (!this.userIsSelected) {
                return null;
            }
            if (!userStyle || !this.state.allStyles || this.state.allStyles.length < 1) {
                return (<LoadingIndicator />);
            }

            const styleTexts = userStyle.styleTexts || [];
            const sortedAdditionalStyles = styleTexts.filter((styleText: StyleText) => {
                return styleText.styleTextType === 'additional';
            }).sort((one: StyleText, two: StyleText) => {
                if (one.sort > two.sort) {
                    return 1;
                } else if (one.sort < two.sort) {
                    return -1;
                }
                return 0;
            });

            return (
                <>
                    {/*<div className='content-header'>*/}
                    {/*    <div className='content-container'>*/}
                    {/*        <div className='col-sm-10 col-md-8 col-xs-12'>*/}
                    {/*            <div className='title'>*/}
                    {/*                <div className="select-box">*/}
                    {/*                    {this.selectedUserData && this.selectedUserData.name && <span>{this.selectedUserData.name}</span>}*/}
                    {/*                    {this.selectedUserData && this.selectedUserData.style && <PVABadge style={this.selectedUserData.style.name} badge={this.selectedUserData.style.name[0]} />}*/}
                    {/*                </div>*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    <FoundationsReportContent
                        {...this.props}
                        UserStore={this.props.UserStore}
                        notificationService={this.props.notificationService}
                        history={this.props.history}
                        location={this.props.location}
                        match={this.props.match}
                        useContentOnly={true}
                        hideDichotomies={false}
                    />
                </>
            );
        }

        let riAndRbComponent = null;
        if (this.userCanSeeRIandRB) {
            const rbContent = this.buildResponseBreakdownContent();
            var offset = 0;

            riAndRbComponent = (
                <div className="response-indicators-and-breakdowns">
                    {
                        this.selectedUserData && this.selectedUserData.inclinationGroups.filter(this.filterResponseIndicators.bind(this)).map((groupData: any, index: number) => {
                            var riContent = this.buildResponseIndicator(groupData, offset);
                            offset += groupData.data.length;
                            return <PVAContainer id={`ri-container-${groupData.id}`} title={groupData.name} open={false}>
                                {riContent}
                            </PVAContainer>;
                        })
                    }
                    <PVAContainer id={`rb-container`} title={'Response Breakdown'} open={false}>
                        {rbContent}
                    </PVAContainer>
                </div>
            );
        }

        // Makes the bar charts
        var resultGroups: any = <div className="result-group-empty-div" />;
        if (!this.props.loading && this.selectedUserData) {
            var indexOffset = 0;
            const normalIndicators = this.selectedUserData.inclinationGroups.filter(this.filterNormalIndicators.bind(this));
            
            if (normalIndicators.length > 0) {
                resultGroups = (
                    <>
                        {this.selectedUserData.inclinationGroups.filter(this.filterNormalIndicators.bind(this)).map((groupData: any, index: number) => {
                            let chart = (<GroupChart key={index} teamID={this.GetTeamID()} style={userStyle} groupData={groupData} reportType={this.reportType} midRangeType={this.midRangeType} index={index} offset={indexOffset} headerOffset={true} small={this.small} allowToggle={this.props.UserStore!.checkUserPermission(UserPermissions.ScoreBarColorChange)}/>);
                            indexOffset += groupData.data.length;
                            if (groupData.subGroups) {
                                groupData.subGroups.map((sg: any) => {
                                    indexOffset += sg.data.length;
                                });
                            }

                            return chart;
                        })}
                        {/*{this.userCanSeeRIandRB && this.selectedUserData && this.selectedUserData.inclinationGroups.filter(this.filterResponseIndicators.bind(this)).map((groupData: any, index: number) => {*/}
                        {/*    let chart = (<GroupChart key={index} teamID={this.GetTeamID()} style={userStyle} groupData={groupData} reportType={this.reportType} midRangeType={this.midRangeType} index={index} offset={indexOffset} headerOffset={true} small={this.small} allowToggle={this.props.UserStore!.checkUserPermission(UserPermissions.ScoreBarColorChange)}/>);*/}
                        {/*    indexOffset += groupData.data.length;*/}
                        {/*    if (groupData.subGroups) {*/}
                        {/*        groupData.subGroups.map((sg: any) => {*/}
                        {/*            indexOffset += sg.data.length;*/}
                        {/*        });*/}
                        {/*    }*/}
                        
                        {/*    return chart;*/}
                        {/*})}*/}
                    </>);
            }
        }

        const notesTable = (
        <table className="table table-striped">
            <thead>
                <tr>
                    <th className="table-column-thin">Created</th>
                    <th className="table-column-thin">Added By</th>
                    <th className="table-column-wide">Note</th>
                    <th className="table-column-thin" />
                </tr>
            </thead>
            <tbody className="vertical-align-cells">
                {this.state.notes.map((note: SurveyNote, ind: number) => {
                    return <tr key={`note-${note.id}`}>
                        <td>{moment(note.created).format('L LT')}</td>
                        <td>{note.createdByLastName}, {note.createdByFirstName}</td>
                        <td>{note.value}</td>
                        <td>
                            { this.loggedInUserId === note.createdById &&
                                <button className="btn btn-danger" onClick={() => this.deleteNote(note.id)}>Remove Note</button>
                            }
                        </td>
                    </tr>;
                })}
            </tbody>
        </table>);

        const noteForm = (
            <>
                <div className="row">
                    <div className="col-xs-11">
                        <label>Note</label>
                        <textarea className="form-control" onChange={(e) => this.setState({ workingNote: e.target.value })} value={this.state.workingNote} />
                    </div>
                    <div className="col-xs-1">
                        <label>Private</label>
                        <input type="checkbox" className="form-control" checked={this.state.private} onChange={(e) => this.setState({private: !this.state.private})} />
                    </div>
                </div>

                <br />
                <button className="btn btn-primary" disabled={this.state.workingNote.length === 0} onClick={(e) => this.createNote()}>Add</button>
            </>
        );

        const body = (
            <>
                <>
                    {this.props.pdfLoader && <LoadingIndicator showPdfDescription={true} showInPanel={true} showBackground={true}/>}

                    {!this.props.pdfLoader && this.selectedUserData && this.userHasCompletedPVA && (
                        userStyle ? (
                            <PVAStyle 
                                notificationService={this.props.notificationService} 
                                history={this.props.history}
                                location={this.props.location}
                                match={this.props.match}
                                name={this.selectedUserData.name || ' '} 
                                style={userStyle} 
                                brief={userStyle.brief} 
                                content={userStyle.content} 
                                core={userStyle.core} 
                                showSecondaryStyle={true} 
                                onViewAll={() => this.props.history.push('/pvastyles')}
                                surveyVersion={this.surveyVersion}
                                allStyles={this.state.allStyles}
                            />
                        ) : (
                            !this.selectedUserData.default && (
                                <div className="alert alert-info">
                                    No style information available for this user.
                                </div>
                            )
                        )
                    )}
                    
                    {(this.userIsSelected && !this.userHasCompletedPVA && <div className="alert alert-info">
                        User has not completed the PVA.
                    </div>)}
                    
                    {!this.props.pdfLoader && this.selectedUserData && !this.showNotes &&
                        <>
                            {resultGroups}
                            {this.userIsSelected && riAndRbComponent}
                        </>
                    }
                </>

                {(this.props.UserStore!.userRoleIn([Roles.SiteAdmin, Roles.SuperAdmin]) || this.props.UserStore!.checkUserPermission(UserPermissions.ViewAssessmentNotes)) && this.showNotes && this.selectedUserData && 
                    <>
                        <PVAContainerSimple title={'Notes'} defaultOpen={true}>
                            {notesTable}
                        </PVAContainerSimple>
                        <PVAContainerSimple title={'Add New Note'} defaultOpen={false}>
                            {noteForm}
                        </PVAContainerSimple>
                    </>
                }

                <div className="text-right" style={{color: '#ccc'}}>
                    <>
                        {this.selectedUserData && this.selectedUserData.surveyVersionName}                            
                        {this.selectedUserData && this.selectedUserData.styleOverridden && <span> <small>*</small></span>}
                    </>
                    <>{this.getDateString()}</>
                </div>
            </>
        );

        // if (this.props.showDropDown) {
        //     return (
        //         <PVAPage ref={(e: any) => this.pageTop = e}>
        //             {header}
        //             {body}
        //         </PVAPage>
        //     );
        // } else {
        return body;
    }

    private filterResponseIndicators(x: any) {
        if (x.displayType !== 'responseIndicator') {
            return false;
        }
        if (!this.surveyReportType) {
            return true;
        }
        return x.surveyReportTypeID === this.surveyReportType.id;
    }

    private filterNormalIndicators(x: any) {
        if (x.displayType !== 'normal') {
            return false;
        }
        if (!this.surveyReportType) {
            return true;
        }
        return x.surveyReportTypeID === this.surveyReportType.id;
    }

    getDateString() {
        if (!this.selectedUserData || !this.selectedUserData.completed) {
            return '';
        }

        var d = new Date(this.selectedUserData.completed);
        return (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear() + ' ' + d.getHours() + ':' + d.getMinutes();
    }
    
    get userIsSelected(): boolean {
        return this.props.userId !== undefined;
    }

    get userHasCompletedPVA(): boolean {
        return !!(this.selectedUserData && this.selectedUserData.completed &&
            this.selectedUserData.inclinationGroups &&
            this.selectedUserData.inclinationGroups.filter(this.filterNormalIndicators.bind(this)).length > 0);
    }

    deleteNote(noteId: number) {
        const reallyDelete = confirm('Are you sure you want to delete this note?');

        if (reallyDelete) {
            this.userDataService.deleteNote(noteId)
                .then(() => {
                    this.props.notificationService.addNotification('Note removed successfully.');

                    this.setState({notes: this.state.notes.filter((x) => x.id !== noteId)});
                })
                .catch(() => this.props.notificationService.addError('Error removing note, please try again.'));
        }
    }

    createNote() {
        var note = new SurveyNote();
        note.value = this.state.workingNote;
        note.private = this.state.private;
        this.userDataService.createNote(this.selectedUserData.surveyInstanceId, note)
            .then((savedNote: SurveyNote) => {
                this.props.notificationService.addNotification('Note successfully added.');
                var notes = this.state.notes;
                notes.push(savedNote);
                this.setState({
                    notes: notes,
                    workingNote: ''
                });
            })
            .catch(() => this.props.notificationService.addError('Error adding note, please try again.'));
    }

    toggleModal(index: number) {
        let selectedUserData = this.selectedUserData;

        var offset = 0;
        selectedUserData.inclinationGroups.filter(this.filterResponseIndicators.bind(this)).map((groupData: any) => {
            var data = groupData.data.sort(this.basicSort);
            data.map((c: any, cInd: number) => {
                if (offset + cInd === index) {
                    c.showModal = true;
                } else {
                    c.showModal = false;
                }
            });
            offset += data.length;
        });

        this.forceUpdate();
    }

    private basicSort(a: any, b: any) {
        if (a.sort > b.sort) {
            return 1;
        }
        if (a.sort < b.sort) {
            return -1;
        }
        return 0;
    }

    private buildResponseIndicator(groupData: any, off: number) {
        var data = groupData.data.sort(this.basicSort);

        return (
            <div className="response-indicator">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <td />
                            <td>Indicator</td>
                            <td className="text-right">Score</td>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((c: any, cIndex: number) => {
                            var offset = off + cIndex;
                            return <tr key={`group-${groupData.characteristicGroupID}-index-${cIndex}`}>
                                <td>
                                    <span onClick={(e) => this.toggleModal(offset)} className="characteristic-info glyphicon glyphicon-info-sign icon-button" />
                                </td>
                                <td>
                                    <span onClick={(e) => this.toggleModal(offset)}>{c.name}</span>
                                </td>
                                <td className="text-right">
                                    <span>{c.value.toFixed(0)}%</span>
                                </td>
                            </tr>;
                        })}
                    </tbody>
                </table>
                {
                    data.map((c: any, cIndex: number) => {
                        const props = {
                            show: c.showModal,
                            onHide: this.toggleModal.bind(this, -1),
                            title: c.name,
                            topic: ' ',
                            content: c.helpText
                        };

                        return (
                            <div className="ri-modal-div" key={`ri-modal-${off + cIndex}`}>
                                <PonderaModal {...props} />
                            </div>);
                    })
                }
            </div>
        );
    }

    private buildResponseBreakdownContent() {
        const selectedUserData = this.selectedUserData;

        if (selectedUserData && selectedUserData.responseBreakdowns) {
            const totalCount = selectedUserData.responseBreakdowns.reduce((prev: any, curr: any) => prev + curr.count, 0);
            const totalPercentage = selectedUserData.responseBreakdowns.reduce((prev: any, curr: any) => prev + curr.percentage, 0);

            return (
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <td>Response</td>
                            <td className="text-right">Count</td>
                            <td className="text-right">Percentage</td>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedUserData.responseBreakdowns.map((rb: any) => {
                            return <tr key={`index-${rb.value}`}>
                                <td>{rb.value}</td>
                                <td className="text-right">{rb.count}</td>
                                <td className="text-right">{rb.percentage.toFixed(1)}%</td>
                            </tr>;
                        })}
                        <tr>
                            <td>Total</td>
                            <td className="text-right">{totalCount}</td>
                            <td className="text-right">{totalPercentage.toFixed(1)}%</td>
                        </tr>
                    </tbody>
                </table>);
        }

        return null;
    }
    
    public renderBriefReport() {
        const style = this.getUserStyle();
        const userName = this.selectedUserData ? this.selectedUserData.name : '';
        const date = moment().format('MMMM D, YYYY');
        const organization = this.props.organization ? this.props.organization.name : '';

        return <Pdf>
            <Pdf.TitlePage 
                title="Pondera Virtual Advisor" 
                subtitle="Individual Brief" 
                name={userName}
                date={date}
                organization={organization}
            />
            <Pdf.Page>
                <PVAStyle 
                    notificationService={this.props.notificationService} 
                    history={this.props.history}
                    location={this.props.location}
                    match={this.props.match}
                    name={this.selectedUserData.name || ' '} 
                    style={this.selectedUserData.style} 
                    brief={this.selectedUserData.style.brief} 
                    content={this.selectedUserData.style.content} 
                    core={this.selectedUserData.style.core} 
                    showSecondaryStyle={true} 
                    hideButtons={true} 
                    onViewAll={() => this.props.history.push('/pvastyles')}
                    surveyVersion={this.surveyVersion}
                    allStyles={this.state.allStyles}
                />
            </Pdf.Page>
        </Pdf>
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/blocks/IndividualReportBlock.tsx